<script lang="ts" setup>
    import { onMounted, ref, watch, computed } from 'vue';
    import ChoiceTable from '../../components/ChoiceTable.vue';
    import { ISoapNoteListItem, ISoapNotesCounts } from '../../lib/Models/SoapNotes';
    import * as ChoiceApi from '../../lib/ChoiceApi'
    import { CellClassDelegate, CellStyleDelegate, ICol, RenderModeType } from '../../lib/ChoiceTableHelpers';
    import { ChoiceEntityUtils } from '../../LibMain';
    import { GenderType, SoapNotesType } from '../../Enums/ChoiceEnums';
    import { getAge } from '../../lib/DateUtils';
    import { TableRowType, ButtonDisplaySize } from '../../Enums/UiEnums';
    import LoadingSection from '../../components/LoadingSection.vue';
    import DropDownButton from '../../components/DropDownButton.vue';
    import DropDownButtonItem from '../../components/DropDownButtonItem.vue';
    import { IUserInfo } from '../../lib/Models/UserInfoModel';
    import * as _ from 'lodash'
    import OptionsDropPanel from '../../components/OptionsDropPanel.vue'
    import MessageText from '../../components/Messages/MessageText.vue';
    import * as Notification from '../../lib/Notification'
    import * as FormattingEntity from '../../lib/Formatting/Entity'

    const props = defineProps<{
        type?: SoapNotesType,
        userId: string
    }>();

    const emit = defineEmits(["update:type", "update:userId"]);
    const userOptionsCtrl = ref();

    const isLoadingData = ref(false);
    const notes = ref<ISoapNoteListItem[]>([]);
    const users = ref<IUserSoapNoteCounts[]>([]);
    const userCountsLoaded = ref(false);

    interface IUserSoapNoteCounts extends IUserInfo
    {
        openCnt?: number, 
        missingCnt?: number, 
        countTotal?: number        
    }

    onMounted(async () => {
        if (null === props.userId || undefined === props.userId || "" === props.userId?.trim())
        {
            alert("Problem preparing to fetch SOAP notes: userID not specified");
        }

        // Get the user info 
        GetUserInfoAsync();

        await UpdateSoapNotes(props.userId, props.type);
    });

    async function GetUserInfoAsync()
    {
        const userPromise = ChoiceApi.user.AllUsers().then(resp => users.value = resp);

        let userCounts: ISoapNotesCounts[] = [];
        const userCountsPromise = ChoiceApi.soapNote.DashboardCounts().then(resp => userCounts = resp);

        await Promise.all([userPromise, userCountsPromise]);

        // Apply the counts to the users
        for(const user of users.value)
        {
            const userCountData = userCounts.find(cnt => cnt.userId === user.userId);
            if (userCountData)
            {
                user.openCnt = userCountData.openCnt;
                user.missingCnt = userCountData.missingCnt;
                user.countTotal = userCountData.countTotal;
            }
        }
        
        const visibleUsers = userCountsLoaded ? _.filter(users.value, user => user.countTotal || user.userId === props.userId) as IUserSoapNoteCounts[] : users.value;
        users.value = _.orderBy(visibleUsers, ['active', 'lastName','firstName'], ['desc', 'asc', 'asc']);
    }

    // Update the SOAP notes
    async function UpdateSoapNotes(userId: string, noteType?: SoapNotesType)
    {
        isLoadingData.value = true;
        try
        {
            // Order notes by patient name & Encounter Date
            const respNotes = await ChoiceApi.soapNote.List(userId, noteType);
            notes.value = _.orderBy(respNotes, ["patient_name", "encounter_date"]);
        }
        catch(e)
        {
            alert(`Error getting SOAP notes: ${(e as Error)?.message}`)
        }        
        userOptionsCtrl.value?.hideOptions();
        isLoadingData.value = false;
    }

    // Make text for negative values red
    const MutedData: CellClassDelegate = (_col: ICol, _row: Record<string, any>, _rowType: TableRowType): string[] | undefined => {
        if (_rowType === TableRowType.Body)
            return ["text-muted"];
    }
    const SmallText: CellStyleDelegate = (_col: ICol, _row: Record<string, any>, _rowType: TableRowType): Record<string, string> | undefined => {
        if (_rowType === TableRowType.Body)
        {
            const styles: Record<string, string> = {};
            styles["font-size"] = "small";
            return styles;
        }
    }    


    const soapNoteTableColumns = computed<ICol[]>(() => { 
            const columns: ICol[] = [
                { Name: "Patient", DataField: "patient_name" },
                { Name: "Gender", DataField: "gender_code", FormatBodyValue: GenderText, ClassDel: MutedData, StyleDel: SmallText },
                { Name: "DOB", DataField: "birth_date", RenderMode: RenderModeType.PrettyDate, ClassDel: MutedData, StyleDel: SmallText },
                { Name: "Age", DataField: "birth_date", FormatBodyValue: Age, ClassDel: MutedData, StyleDel: SmallText },
                { Name: "Encounter Date", DataField: "encounter_date", RenderMode: RenderModeType.PrettyDate },
                { Name: "Note Type", DataField: "soap_note_type" },
                { Name: "Chief Complaint", DataField: "chief_complaint", RenderMode: RenderModeType.HTML },
                { Name: "Sign Off", DataField: "primary_signoff", FormatBodyValue: SignOffText },
                { Name: "Created By", DataField: "owner_name" }
                ];
            
            if (!props.type)
                columns.push({ Name: "Note Status", DataField: "is_missing_soap_note", FormatBodyValue: (isMissing) => isMissing ? "Missing" : "Open" });

            // Do we need a button action?  Add to the first column
            if (!props.type || props.type === SoapNotesType.Missing)
                columns.unshift({ Name:"", ExtraData: { ActionColumn: true }, HideFilter: true });
                
            return columns;
        }
    );

    const userTableColumns: ICol[] = [
        {Name: "User Name", DataField: "lastName" },
        {Name: "First Name", DataField: "firstName" },
        {Name: "Status", DataField: "active", FormatBodyValue:(active) => active ? "Active" : "Inactive" },
        {Name: "# Open Notes", DataField: "openCnt", ExtraData:{ SoapNoteValue: true, CntType: SoapNotesType.Open } },
        {Name: "# Missing Notes", DataField: "missingCnt", ExtraData:{ SoapNoteValue: true, CntType: SoapNotesType.Missing } },
        {Name: "# Total Notes", DataField: "countTotal", ExtraData:{ SoapNoteValue: true } }
    ];


    function Age(value: any, _col: ICol, _row: Record<string, any>): string | undefined
    {
        return value ? `${getAge(value)}` : undefined;
    }

    function SignOffText(value: any, _col: ICol, _row: Record<string, any>): string | undefined
    {
        return "Y" === value ? "Awaiting Primary Signoff" : undefined;
    }

    function GenderText(value: any, _col: ICol, _row: Record<string, any>): string | undefined
    {
        return ChoiceEntityUtils.genderDescFromCode(value);
    }

    const CurrentNoteTypeDesc = computed(() => {
            switch(props.type)
            {
                case SoapNotesType.Open:
                    return "Open";
                case SoapNotesType.Missing:
                    return "Missing";
                default:
                    return "All";
            }
        });

    function OnChangeNoteType(newNoteType: SoapNotesType){
        if (props.type === newNoteType)
            return;

        emit("update:type", newNoteType);

        UpdateSoapNotes(props.userId, newNoteType);
    }

    function OnChangeUser(user: IUserInfo)
    {
        emit("update:userId", user.userId);

        // Update the SOAP notes
        UpdateSoapNotes(user.userId, props.type);
    }

    const CurrentUserName = computed(()=>
        FormattingEntity.standardName(users.value.find(user => user.userId == props.userId))
    );

    watch(() => props.userId, (_new, _old) => {
        userOptionsCtrl.value?.hideOptions();
    });

    const orderedUsers = computed(() => {
        const visibleUsers = userCountsLoaded ? _.filter(users.value, user => user.countTotal || user.userId === props.userId) : users.value;
        return _.orderBy(visibleUsers, ['active', 'lastName','firstName'], ['desc', 'asc', 'asc']);
    });

    function SetUserAndNoteType(userId: string, noteType?: SoapNotesType)
    {
        emit("update:userId", userId);
        emit("update:type", noteType);

        UpdateSoapNotes(userId, noteType);
    }

    // TODO: Add Bypass
    async function BypassSoapNote(soapNote: ISoapNoteListItem)
    {
        if (false === await Notification.confirmDialog("Bypass the SOAP note?", {ConfirmText: "Bypass", RejectText: "Cancel"}))
            return;

        if (await ChoiceApi.soapNote.Bypass(soapNote.appointment_id))
        {
            // Update the list
            notes.value = _.filter(notes.value, note => soapNote.appointment_id !== note.appointment_id);
        }
    }

</script>

<template>

    <div>
        <OptionsDropPanel ref="userOptionsCtrl">
            <template #title>User:</template>
            <template #options>
                <ChoiceTable
                    :columns="userTableColumns" 
                    :data="users" 
                    :allowSorting="true" 
                    :showFilters="true" 
                    keyValueDataMember="user_id">
                    <template #cell="{col, row, value}">
                        <template v-if="col.ExtraData?.SoapNoteValue">
                            <a style="cursor:pointer" class="text-primary" @click="SetUserAndNoteType(row.userId, col.ExtraData?.CntType)">{{value}}</a>
                        </template>
                    </template>
                </ChoiceTable>
            </template>
            <template #summary>
                <MessageText v-if="CurrentUserName" class="ms-1">
                    <em class="fa-solid fa-user"></em>
                    {{CurrentUserName}}
                </MessageText>

                <!-- TODO: btn btn-light dropdown-toggle dropdown-toggle-split text-primary OR outline-primary -->
                Note Type: 
                <DropDownButton buttonTitle="Change SOAP note type" :size="ButtonDisplaySize.Small">
                    <template #button>{{CurrentNoteTypeDesc}}</template>
        
                    <DropDownButtonItem :data="SoapNotesType.Open" @click="OnChangeNoteType">Open</DropDownButtonItem>
                    <DropDownButtonItem :data="SoapNotesType.Missing" @click="OnChangeNoteType">Missing</DropDownButtonItem>
                    <DropDownButtonItem @click="OnChangeNoteType">All</DropDownButtonItem>
                </DropDownButton>        
            </template>
        </OptionsDropPanel>

    </div>
    
    <LoadingSection :isLoading="isLoadingData" loadingMsg="Loading Data...">
        Note Count: <MessageText>{{notes.length}}</MessageText>
        
        <ChoiceTable 
            :columns="soapNoteTableColumns" 
            :data="notes" 
            :allowSorting="true" 
            :showFilters="true" 
            :keyValueDataMember="'soap_note_id'">
            <template #cell="{col, row, value}">
                <template v-if="col.ExtraData?.ActionColumn">
                    <button class="btn btn-primary print-none" 
                                title="Bypass SOAP Note"
                                v-if="(row as ISoapNoteListItem).is_missing_soap_note"
                                @click="BypassSoapNote(row as ISoapNoteListItem)">
                        <em class="fa-solid fa-xmark"></em>
                    </button>
                </template>
                <template v-if="'Gender' === col.Name">
                    <em class="fa-solid fa-mars" v-if="value === GenderType.Male"></em>
                    <em class="fa-solid fa-venus" v-if="value === GenderType.Female"></em>
                    <em class="fa-solid fa-genderless" v-if="value === GenderType.Unknown"></em>
                    {{ChoiceEntityUtils.genderDescFromCode(value)}}
                </template>
            </template>
        </ChoiceTable>
    </LoadingSection>

</template>